import React, { useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import {
  TrendingUp, Layout, Handshake,
  Waypoints, FileText, Cpu, Megaphone,
  Globe, Bot, Brain, Share2,
  MessageCircle, Ear, Target, Rocket, RefreshCw,
  BarChart3, Users, Building2
} from 'lucide-react';
import TextReveal from '../components/TextReveal';
import RoundedButton from '../components/RoundedButton';

gsap.registerPlugin(ScrollTrigger);

/* ─── Animation variants ─── */
const heroSlideUp = {
  initial: { y: 300 },
  enter: { y: 0, transition: { duration: 0.6, ease: [0.33, 1, 0.68, 1], delay: 2.5 } }
};

/* ─── Infinite Slider ─── */
function InfiniteSlider() {
  const firstText = useRef(null);
  const secondText = useRef(null);
  const slider = useRef(null);
  const xPercent = useRef(0);
  const direction = useRef(-1);

  useLayoutEffect(() => {
    const animateLoop = () => {
      if (xPercent.current < -100) xPercent.current = 0;
      else if (xPercent.current > 0) xPercent.current = -100;
      if (firstText.current && secondText.current) {
        gsap.set(firstText.current, { xPercent: xPercent.current });
        gsap.set(secondText.current, { xPercent: xPercent.current });
      }
      requestAnimationFrame(animateLoop);
      xPercent.current += 0.1 * direction.current;
    };

    gsap.to(slider.current, {
      scrollTrigger: {
        trigger: document.documentElement,
        scrub: 0.25,
        start: 0,
        end: window.innerHeight,
        onUpdate: e => direction.current = e.direction * -1,
        invalidateOnRefresh: false
      },
      x: "-500px",
    });
    requestAnimationFrame(animateLoop);
    ScrollTrigger.config({ ignoreMobileResize: true });
    return () => ScrollTrigger.getAll().forEach(t => t.kill());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="hero-slider-container">
      <div ref={slider} className="hero-slider">
        <p ref={firstText}>Growth Partner &mdash;</p>
        <p ref={secondText}>Growth Partner &mdash;</p>
      </div>
    </div>
  );
}

/* ─── Scroll-triggered Section ─── */
function ScrollSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-15%" });

  return (
    <motion.section
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
    >
      {children}
    </motion.section>
  );
}

/* ─── Why Item with stagger ─── */
function WhyItem({ num, icon: Icon, title, text, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <motion.div
      ref={ref}
      className="why-item"
      initial={{ opacity: 0, x: -40 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.33, 1, 0.68, 1], delay }}
    >
      <div className="why-num-wrap">
        <span className="why-num">{num}</span>
        <Icon className="why-icon" size={28} strokeWidth={1.2} />
      </div>
      <div className="why-body">
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </motion.div>
  );
}

/* ─── Service Item ─── */
function ServiceItem({ num, icon: Icon, title, desc, to, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-5%" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      <Link to={to} className="svc-item">
        <div className="svc-num-wrap">
          <span className="svc-num">{num}</span>
          <Icon className="svc-icon" size={24} strokeWidth={1.2} />
        </div>
        <div className="svc-body">
          <h3>{title}</h3>
          <p>{desc}</p>
        </div>
        <span className="svc-arrow">&rarr;</span>
      </Link>
    </motion.div>
  );
}

/* ─── Flow Step ─── */
function FlowStep({ num, icon: Icon, title, text, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <motion.div
      ref={ref}
      className="flow-step"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      <div className="flow-num-wrap">
        <Icon className="flow-icon" size={18} strokeWidth={1.5} />
        <span className="flow-num">{num}</span>
      </div>
      <div className="flow-content">
        <h4>{title}</h4>
        <p>{text}</p>
      </div>
    </motion.div>
  );
}

/* ─── Contact/CTA with spring Y ─── */
function ContactCTA() {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start end", "end end"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [-300, 0]);

  return (
    <motion.section style={{ y }} ref={container} className="section-contact">
      <div className="contact-inner">
        <div className="contact-title">
          <span>
            <h2>共に成長できる</h2>
          </span>
          <h2>パートナーを。</h2>
        </div>
        <p className="contact-sub">
          私たちは、すべてのご相談をお受けしているわけではありません。<br />
          商品やサービスに真剣に向き合い、共に成長していける関係を大切にしています。
        </p>
        <div className="contact-btn-wrap">
          <Link to="/contact">
            <RoundedButton>
              <p>ご相談はこちら</p>
            </RoundedButton>
          </Link>
        </div>
        <div className="contact-footer">
          <div className="contact-info">
            <span>
              <h3>PerfTech Inc.</h3>
              <p>&copy; 2025 Edition</p>
            </span>
          </div>
          <div className="contact-nav">
            <Link to="/service">Service</Link>
            <Link to="/company">Company</Link>
            <Link to="/recruit">Recruit</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

/* ─── Parallax Gallery ─── */
function ParallaxGallery() {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start end", "end start"]
  });
  const x1 = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const x2 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const height = useTransform(scrollYProgress, [0, 0.9], [50, 0]);

  const row1 = [
    'Performance Marketing',
    'Owned Media',
    'RPA Automation',
    'AI Implementation',
  ];
  const row2 = [
    'Full-Funnel Design',
    'Technology Leverage',
    'Growth Partner',
    'SNS Automation',
  ];

  return (
    <div ref={container} className="parallax-gallery">
      <motion.div style={{ x: x1 }} className="parallax-row">
        {row1.map((item, i) => (
          <div key={i} className="parallax-card">
            <span>{item}</span>
          </div>
        ))}
      </motion.div>
      <motion.div style={{ x: x2 }} className="parallax-row">
        {row2.map((item, i) => (
          <div key={i} className="parallax-card">
            <span>{item}</span>
          </div>
        ))}
      </motion.div>
      <motion.div style={{ height }} className="parallax-curve-container">
        <div className="parallax-curve"></div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   HomePage
   ═══════════════════════════════════════════════════ */
export default function HomePage() {
  return (
    <div className="home-page">

      {/* ─── Hero ─── */}
      <motion.section
        variants={heroSlideUp}
        initial="initial"
        animate="enter"
        className="hero-section"
      >
        <video
          className="hero-bg-video"
          src="/images/hero-bg.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        />
        <div className="hero-overlay" />
        <div className="hero-content-wrap">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.6 }}
          >
            <p className="hero-tagline">
              <span className="hero-tagline-line">Growth Partner</span>
              <span className="hero-tagline-line">for Your Business</span>
            </p>
            <h1 className="hero-title">
              <span className="title-line"><span>事業の成長を、</span></span>
              <span className="title-line"><span>共に設計する。</span></span>
            </h1>
          </motion.div>
        </div>
        <InfiniteSlider />
        <div className="scroll-indicator">
          <span>Scroll</span>
          <div className="scroll-indicator-line"></div>
        </div>
      </motion.section>

      {/* ─── Mission ─── */}
      <ScrollSection className="section-mission">
        <div className="mission-inner">
          <span className="section-label">Our Mission</span>
          <h2 className="mission-headline">日本のマーケティングを、<br />もっと強くする。</h2>
        </div>
        <div className="mission-marquee">
          <div className="mission-marquee-track">
            {[1,2,3,4,5,6,7,8,9,10,11,12,13,1,2,3,4,5,6,7,8,9,10,11,12,13].map((n, i) => (
              <div key={i} className="mission-marquee-item">
                <img src={`/images/people/person-${String(n).padStart(2,'0')}.jpg`} alt="" />
              </div>
            ))}
          </div>
        </div>
        <div className="mission-inner">
          <p className="mission-body">
            優秀な人材ほど、場所や時間に縛られることを嫌います。<br />
            でも私たちがリモートワークやフレックスを推奨するのは、「自由に働きたい」からではありません。
            <br /><br />
            成果と価値だけにフォーカスするため。<br />
            通勤に費やしていた時間で、クライアントの商品を深く理解する。形式的な報告ではなく、データの奥にある構造を読み解くことに時間を使う。AIに任せられることは任せて、人間にしかできない判断と設計に集中する。
            <br /><br />
            場所や時間を手放した分だけ、成果と価値に向き合える。<br />
            一人ひとりが自分のスキルを最大限に活かしながら、クライアントの事業に本気で向き合える環境をつくる。それが、強いマーケティングの出発点だと考えています。
            <br /><br />
            そうやって商品の本来のポテンシャルを引き出せるマーケターを、日本からもっと増やしていく。それが、PerfTechの目指す景色です。
          </p>
          <div className="mission-btn">
            <Link to="/recruit">
              <RoundedButton>
                <p>Join Us</p>
              </RoundedButton>
            </Link>
          </div>
        </div>
      </ScrollSection>

      {/* ─── Description & Philosophy ─── */}
      <section className="section-description">
        <div className="description-inner">
          <TextReveal
            phrase="正しく届ける、という仕事。"
            subtext={"商品やサービスに力があるのに伸び悩んでいるなら、ポテンシャルが正しく届いていないだけかもしれない。\n手法の先へ。本来の力が顧客に正しく届く構造を設計しています。「選ばれる理由」そのものをつくること。それを私たちは、マーケティングと呼んでいます。"}
          />
        </div>
        <div className="section-inner description-philosophy">
          <div className="section-header">
            <span className="section-label">Our Philosophy</span>
            <h2 className="section-title">私たちが、大切にしていること</h2>
          </div>
          <div className="why-list">
            <WhyItem
              num="01"
              icon={TrendingUp}
              title="商品のポテンシャルに向き合う"
              text="私たちが最初に見るのは、マーケティングの数字ではなく、商品やサービスそのものの力です。ポテンシャルのある商品には、正しく届ける方法が必ずある。その可能性を信じることが、すべての出発点です。"
              delay={0}
            />
            <WhyItem
              num="02"
              icon={Layout}
              title="届く構造を設計する"
              text="集客の手法だけでは、成長は続かない。商品の価値が正しく伝わり、理解された上で選ばれる。その一連の構造を設計することが、私たちの仕事の中心にあります。"
              delay={0.1}
            />
            <WhyItem
              num="03"
              icon={Handshake}
              title="クライアントと同じ方向を向く"
              text="私たちの報酬は、クライアントの事業成果に連動しています。同じゴールを共有するからこそ、本気で向き合える。パートナーとして対等な関係であることを、大切にしています。"
              delay={0.2}
            />
          </div>
          <div className="description-btn">
            <Link to="/company">
              <RoundedButton>
                <p>About us</p>
              </RoundedButton>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Parallax Keywords ─── */}
      <ParallaxGallery />

      {/* ─── Services ─── */}
      <section className="section-services">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-label">Service</span>
            <h2 className="section-title">成長を設計する、4つの領域</h2>
          </div>
          <div className="svc-list">
            <ServiceItem num="01" icon={Waypoints} title="グロースデザイン" desc="ファネル分析×コホート分析を軸に、商品やサービスのポテンシャルが正しく届く成長構造を設計します。" to="/service#growth-design" delay={0} />
            <ServiceItem num="02" icon={FileText} title="メディア＆コンテンツ" desc="オウンドメディア構築、認知設計コンテンツ、LP制作など「選ばれる理由」をつくる情報設計を行います。" to="/service#media-content" delay={0.08} />
            <ServiceItem num="03" icon={Cpu} title="テクノロジー" desc="RPA、AI活用、SNS自動化など、仕組みの力で成長を加速させるテクノロジーの実装を支援します。" to="/service#technology" delay={0.16} />
            <ServiceItem num="04" icon={Megaphone} title="広告・集客最適化" desc="広告運用、SEO、A/Bテストなど、届ける手段の精度を高める実行支援を行います。" to="/service#acquisition" delay={0.24} />
          </div>
        </div>
      </section>

      {/* ─── Achievement ─── */}
      <ScrollSection className="section-achievement">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-label">Achievement</span>
            <h2 className="section-title">数字が語る、私たちの仕事</h2>
          </div>
          <div className="achievement-grid">
            <div className="achievement-card">
              <BarChart3 className="achievement-icon" size={32} strokeWidth={1} />
              <div className="achievement-number">100 → 1,500<span className="achievement-unit">件/月</span></div>
              <p className="achievement-desc">通販事業のファネル設計からコンテンツ戦略まで一貫支援。月間受注件数を15倍に成長。</p>
            </div>
            <div className="achievement-card">
              <Users className="achievement-icon" size={32} strokeWidth={1} />
              <div className="achievement-number">60<span className="achievement-unit">万人/月</span></div>
              <p className="achievement-desc">オウンドメディアの設計・構築・グロース支援。月間60万人が訪れるメディアを運営。</p>
            </div>
            <div className="achievement-card">
              <Building2 className="achievement-icon" size={32} strokeWidth={1} />
              <div className="achievement-number">National Client</div>
              <p className="achievement-desc">日本を代表する大手企業の新規マーケティング施策を水面下で支援。表には出ずとも、成長の基盤をつくっています。</p>
            </div>
          </div>
        </div>
      </ScrollSection>

      {/* ─── Flow ─── */}
      <section className="section-flow">
        <div className="section-inner flow-layout">
          <div className="flow-header">
            <span className="section-label">Flow</span>
            <h2 className="flow-lead">ご相談から<br />支援開始までの流れ</h2>
          </div>
          <div className="flow-timeline">
            <div className="flow-line" />
            <FlowStep num="01" icon={MessageCircle} title="お問い合わせ" text="まずはお気軽にご相談ください。現状の課題やご要望をお聞かせいただくところからスタートします。" delay={0} />
            <FlowStep num="02" icon={Ear} title="ヒアリング" text="事業の全体像、現状の課題、目指すゴールを詳しくお伺いします。" delay={0.08} />
            <FlowStep num="03" icon={Target} title="戦略提案" text="事業全体を見渡した成長戦略をご提案します。報酬体系も含め、共通目標を持てる座組みを設計します。" delay={0.16} />
            <FlowStep num="04" icon={Rocket} title="実行・運用" text="広告運用、メディア構築、RPA導入など、優先度をつけながら段階的に進めていきます。" delay={0.24} />
            <FlowStep num="05" icon={RefreshCw} title="改善・拡大" text="データに基づく振り返りと改善を繰り返し、施策の範囲を拡大していきます。" delay={0.32} />
          </div>
        </div>
      </section>

      {/* ─── Belief ─── */}
      <ScrollSection className="section-belief">
        <div className="belief-inner">
          <span className="belief-label">Our Belief</span>
          <blockquote className="belief-quote">
            商品力の限界が、<br />マーケティングの限界である。
          </blockquote>
          <div className="belief-body">
            <p>
              どんなに優れたマーケティング施策を打っても、商品やサービスそのものに力がなければ、
              一時的な成果は出せても持続的な成長にはつながりません。
            </p>
            <p>
              だからこそ、私たちがパートナーとしてご一緒するのは、
              自社の商品やサービスに真剣に向き合い、その価値を高め続ける意思を持った企業です。
            </p>
            <p>
              選ばれる側であると同時に、選ぶ側でもある。
              私たちはこの姿勢を貫いています。
            </p>
          </div>
        </div>
      </ScrollSection>

      {/* ─── Contact / CTA ─── */}
      <ContactCTA />
    </div>
  );
}
